import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { TruckCard } from "@/components/TruckCard";
import { RouteBlock } from "@/components/RouteBlock";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Send, Loader2 } from "lucide-react";

const SUGGESTIONS = ["Tacos in Green Bay tonight", "Best BBQ near Appleton", "Something sweet in Oshkosh", "Vegan options nearby"];

export default function Concierge() {
  const [truckMap, setTruckMap] = useState({});
  const [messages, setMessages] = useState([
    { role: "concierge", text: "Hey! I'm your FlavorFlow concierge. Tell me what you're craving and where — I'll find trucks that are live right now. 🌮" },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/trucks").then((r) => {
      const m = {}; r.data.forEach((t) => (m[t.id] = t)); setTruckMap(m);
    }).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "customer", text: q }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/concierge", { message: q, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "concierge", text: data.reply, truck_ids: data.truck_ids || [], route: data.route || [], maps_url: data.maps_url, maps_url_apple: data.maps_url_apple }]);
    } catch (e) { toast.error(apiError(e)); setMessages((m) => [...m, { role: "concierge", text: "Sorry, I hit a snag. Try again in a sec." }]); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <div className="flex items-center gap-2 text-teal-600">
        <Sparkles className="w-5 h-5" />
        <p className="text-xs font-bold uppercase tracking-[0.2em]">AI Concierge</p>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">Find your next food fix.</h1>
      <p className="text-slate-500 mt-2">Ask in plain English — I only recommend trucks that are live right now.</p>

      <div className="mt-6 bg-white rounded-2xl border border-teal-100 gr-shadow p-5 min-h-[45vh]" data-testid="concierge-chat">
        <div className="space-y-4">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] ${m.role === "customer" ? "" : "w-full"}`}>
                <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "customer" ? "bg-[#0FB5A6] text-white" : "bg-teal-50 text-slate-800"}`} data-testid={`msg-${m.role}-${i}`}>
                  {m.text}
                </div>
                {m.truck_ids?.length > 0 && (
                  <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid={`concierge-results-${i}`}>
                    {m.truck_ids.map((id) => truckMap[id] && <TruckCard key={id} truck={truckMap[id]} />)}
                  </div>
                )}
                {m.route?.length >= 2 && <RouteBlock route={m.route} mapsUrl={m.maps_url} appleUrl={m.maps_url_apple} />}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start"><div className="rounded-2xl px-4 py-3 bg-teal-50 text-slate-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Finding trucks…</div></div>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => ask(s)} className="px-4 py-2 rounded-full border border-teal-200 text-sm font-semibold text-slate-600 hover:bg-teal-50 hover:text-[#0FB5A6] transition-colors" data-testid={`suggestion-${s}`}>{s}</button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 mt-4 sticky bottom-4">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="What are you craving?" className="rounded-full h-12" data-testid="concierge-input" />
        <Button type="submit" disabled={loading} className="rounded-full h-12 w-12 p-0 bg-[#0FB5A6] hover:bg-[#0E9E92] text-white shrink-0" data-testid="concierge-send"><Send className="w-5 h-5" /></Button>
      </form>
    </div>
  );
}
